import { indexBy, mapObjIndexed, prop } from 'ramda';
import { View } from 'api/views/types';
import { Nothing } from 'core/maybe';
import { Loaded, Creating, Updating, Deleting, Loading } from 'core/state';
import { Views } from './types';

export const empty = {
  byIds: {},
  ids: Nothing(),
};
export const loading = <ViewDefinition>(): Views<ViewDefinition> => ({
  ids: Loading(),
  byIds: {},
});
export const load = <ViewDefinition>(
  views: View<ViewDefinition>[],
): Views<ViewDefinition> => ({
  ids: Loaded(views.map(({ viewId }) => viewId)),
  byIds: mapObjIndexed<View<ViewDefinition>, Loaded<View<ViewDefinition>>>(
    Loaded,
    indexBy(prop('viewId'), views),
  ),
});
export const created =
  <ViewDefinition>(view: View<ViewDefinition>) =>
  ({ ids, byIds }: Views<ViewDefinition>): Views<ViewDefinition> => ({
    ids: Loaded([...(ids[1] ?? []), view.viewId]),
    byIds: { ...byIds, [view.viewId]: Loaded(view) },
  });

export const updating =
  <ViewDefinition>(view: View<ViewDefinition>) =>
  ({
    ids,
    byIds: { [view.viewId]: updatingView, ...rest },
  }: Views<ViewDefinition>): Views<ViewDefinition> => ({
    ids,
    byIds: { ...rest, [view.viewId]: Updating(view, updatingView[1]) },
  });

export const updated =
  <ViewDefinition>(view: View<ViewDefinition>) =>
  ({
    ids,
    byIds: { [view.viewId]: updatingView, ...rest },
  }: Views<ViewDefinition>): Views<ViewDefinition> => ({
    ids,
    byIds: { ...rest, [view.viewId]: Loaded(view) },
  });

export const reverted =
  <ViewDefinition>(view: View<ViewDefinition>) =>
  (views: Views<ViewDefinition>): Views<ViewDefinition> => {
    const {
      ids,
      byIds: { [view.viewId]: updatingView, ...rest },
    } = views;

    if (updatingView[0] !== 'updating') {
      return views;
    }
    const previous = updatingView[2];

    return {
      ids,
      byIds: { ...rest, [view.viewId]: Loaded(previous) },
    };
  };

export const deleting =
  <ViewDefinition>(view: View<ViewDefinition>) =>
  ({
    ids,
    byIds: { [view.viewId]: deletingView, ...rest },
  }: Views<ViewDefinition>): Views<ViewDefinition> => ({
    ids,
    byIds: { ...rest, [view.viewId]: Deleting(view) },
  });

export const deleted =
  <ViewDefinition>(view: View<ViewDefinition>) =>
  ({
    ids,
    byIds: { [view.viewId]: deletedView, ...rest },
  }: Views<ViewDefinition>): Views<ViewDefinition> => ({
    ids: Loaded((ids[1] ?? []).filter((id) => id !== view.viewId)),
    byIds: { ...rest },
  });

export const undeleted =
  <ViewDefinition>(view: View<ViewDefinition>) =>
  ({
    ids,
    byIds: { [view.viewId]: deletingView, ...rest },
  }: Views<ViewDefinition>): Views<ViewDefinition> => ({
    ids,
    byIds: { ...rest, [view.viewId]: Loaded(view) },
  });
export const asList = <ViewDefinition>({
  ids,
  byIds,
}: Views<ViewDefinition>): (
  | Loaded<View<ViewDefinition>>
  | Creating<View<ViewDefinition>>
  | Updating<View<ViewDefinition>>
)[] =>
  (ids[1] ?? [])
    .map((x) => byIds[x])
    .filter(Boolean)
    .filter((x) => x[0] !== 'deleting')
    .map((x) => x as Exclude<typeof x, Deleting<View<ViewDefinition>>>);
